import { forwardRef, useEffect, useState } from "react";
import {
  Box,
  Divider,
  FormControlLabel,
  Grid2,
  IconButton,
  Modal,
  Switch,
  Typography,
  useTheme
} from "@mui/material";
import { Delete, LockPerson, MeetingRoom } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import qrcodeIcon from "@iconify/icons-mdi/qrcode";
import { useSnackbar } from "notistack";
import { InfoSideBar } from "../InfoSidebar";
import { IZoneUserNumber } from "../../../../Domain/Types/FloorPlan/ZoneSchedule";
import { InfoBox } from "../InfoBox";
import { CompactTextField } from "../../../../../Common/CompactTextfield/CompactTextfield";
import { AssignBox } from "../AssignBox/AssignBox";
import { TandemAssignDialogContent } from "../TandemAssignDialog/TandemAssignDialogContent";
import { TandemObjectKind } from "../../../../Domain/Types/FloorPlan/TandemLink.type";
import { tandemPartnerName } from "../../../../Domain/Types/FloorPlan/tandem-link.functions";
import { ZoneInventoryDialogContent } from "./ZoneInventoryDialogContent/ZoneInventoryDialogContent";
import { useInventory } from "../../../../Hooks/useInventory";
import { OptionDialogContent } from "./OptionDialogContent/OptionDialogContent";
import { ZonePermissionDialogContent } from "./ZonePermissionDialogContent/ZonePermissionDialogContent";
import { ZoneAccessControlDialogContent } from "./AccessControlDialogContent/ZoneAccessControlDialogContent";
import { QrCodeComponent } from "../../../Modals/QrCodeModal/QrCodeComponent";
import { useSelector } from "../../../../../../app/helpers";
import { RootState } from "../../../../../../app/rootReducer";
import { MultiInfoHeader } from "../MultiInfoHeader/MultiInfoHeader";
import { MiniTextInput } from "../../../../../Common/MiniTextInput/MiniTextInput";
import { InfoIconWithTooltip } from "../../../../../Title/InfoIcon";
import {
  generateApprovalRequiredLabel,
  generateAutomatedApprovalLabel,
  generateDisabledLabel,
  handleUserNumbersInput,
  initialUserNumbers,
  isEntryAssigned,
  updateAssingedEquipment,
  updateMutatedZoneInventory
} from "./ZoneInfoSidebar.partial";
import { ZoneInfoSidebarProps } from "./ZoneInfoSidebar.switch";
import { IEquipmentInventory } from "../../../../Domain/Types/FloorPlan/Equipment.type";
import { AssignEntryTableContainer } from "../EquipmentCategoryDialogContent/AssignEntryTableContainer";
import { EquipmentCategoryAssignTable } from "../EquipmentCategoryDialogContent/EquipmentCategoryAssignTable";
import { BookingPropertyAssignTable } from "../BookingPropertyDialogContent/BookingPropertyAssignTable";
import { initImageLabel, initMoreItemName } from "../functions/initMoreItemName.function";
import { adjustZoneType } from "../../../Views/CreateFloorPlanView/Tools/useFloorplan/useFloorplan";
import { ButtonStyledText } from "../../../../../Common/ButtonStyledText.partial";
import { FileObject } from "../../../../../../features/FileDropzone/typings/FileObject.type";
import { EnlargedImageCarouselUploadDialog } from "../../../../../../features/EnlargedImageCarousel/enlarged-image-carousel-upload-dialog.component";
import { useRemoteFetchZoneInventory } from "../../../../../../hooks/Remote/Inventory/useRemoteFetchZoneInventoryMutation";
import { extractRelativePath } from "../../../../../ServiceManagement/Roomlayout/functions/extractRelativePath.function";
import { EnlargedImageCarouselDialog } from "../../../../../../features/EnlargedImageCarousel/enlarged-image-carousel-dialog.component";

export const ZoneInfoSidebar = forwardRef<HTMLDivElement, ZoneInfoSidebarProps>(
  (
    {
      height,
      zoneSchedules,
      floorPlan,
      isZnInvModalOpen,
      hasSiteId,
      setIsZnInvModalOpen,
      onClose,
      onClickDelete,
      onChangeZoneDescription,
      onChangeName,
      onChangePerm,
      onChangeEnabledStatus,
      onChangeApproval,
      onChangeEquipCate,
      onChangeBookProp,
      onChangeUserNumber,
      updateZoneInventory,
      inventoryManagement,
      categoryManagement,
      equipmentManagement,
      floorPlanName,
      remoteUnits,
      isRemoteUnitsFetching,
      isEditable,
      setIsFieldEditing
    },
    ref
  ) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();

    const {
      settings: { colorMode }
    } = useSelector((state: RootState) => state.login);

    const iconColor = colorMode === "light" ? "#6f6f6f" : "white";

    const selectedIds = zoneSchedules.map(m => m.id);

    const zoneTypeId =
      zoneSchedules[0]?.zoneTypeId ||
      zoneSchedules[0]?.inventory?.zoneTypeId ||
      zoneSchedules[0]?.inventory?.zoneType?.id;
    const zoneInventoryId = zoneSchedules[0].inventory?.id || zoneSchedules[0].inventoryId;
    const tandemSelf = {
      kind: TandemObjectKind.ZONE,
      inventoryId: zoneInventoryId
    };
    const tandemName = tandemPartnerName(floorPlan, tandemSelf);
    const zoneTypeName =
      adjustZoneType(floorPlan.zoneTypes).find(type => type.id === zoneTypeId)?.name || "";

    const availableEquipment = floorPlan.freeInventories.equipments.filter(
      d => !isEntryAssigned(d, floorPlan.zones)
    );

    // check if multi selected zone type includes conference zone
    const isConferenceOnlySelected = zoneSchedules
      .map(z => z.zoneTypeId || z.inventory?.zoneTypeId)
      .every(v => v === 3);

    // length of selected zone
    const singleZoneSelected = zoneSchedules.length === 1;

    // check if multi selected zone have same type
    const isIdenticalZoneTypeSelected = zoneSchedules
      .map(z => z.zoneTypeId || z.inventory?.zoneTypeId)
      .every(v => v === zoneTypeId);

    const { minUsers, maxUsers } = initialUserNumbers(zoneSchedules);

    const [zoneName, setZoneName] = useState("");
    const [zoneDesc, setZoneDesc] = useState("");
    const [zoneUserNumber, setZoneUserNumber] = useState<IZoneUserNumber>({
      minUsers: minUsers,
      maxUsers: maxUsers
    });

    const [isZoneCategoryModalOpen, setIsZoneCategoryModalOpen] = useState(false);
    const [isEquipmentModalOpen, setIsEquipmentModalOpen] = useState(false);
    const [isZnPermDialogOpen, setIsZnPermDialogOpen] = useState(false);
    const [isAccessControlOpen, setIsAccessControlOpen] = useState(false);
    const [isEquipCateDialogOpen, setIsEquipCateDialogOpen] = useState(false);
    const [isPropertyDialogOpen, setIsPropertyDialogOpen] = useState(false);
    const [isTandemDialogOpen, setIsTandemDialogOpen] = useState(false);
    const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);

    const [files, setFiles] = useState<FileObject[]>([]);
    const [roomPicturePaths, setRoomPicturePaths] = useState<string[]>([]);
    const [picturePathsToDelete, setPicturePathsToDelete] = useState<string[]>([]);

    const [isQrModalOpen, setIsQrModalOpen] = useState(false);
    const [assignedEquipment, setAssignedEquipment] = useState<IEquipmentInventory[]>([]);

    const { handleCreateZoneInventory, mutatedZoneInventory, handlePatchZoneInventory } =
      useInventory({ floorPlan });

    const { mutateAsync: fetchZoneInventory, isLoading: fetchZoneInventoryLoading } =
      useRemoteFetchZoneInventory();

    const saveRoomImages = async () => {
      if (!zoneInventoryId) return;

      const result = await handlePatchZoneInventory({
        zoneInventoryId,
        opts: {
          files: files.map(f => f.file),
          picturePaths: Array.isArray(roomPicturePaths)
            ? roomPicturePaths.map(extractRelativePath)
            : [],
          zoneTypeId
        }
      });

      // update state from mutated zone inventory
      if (result) {
        setRoomPicturePaths(result.picturePaths || []);
        setPicturePathsToDelete([]);
        setFiles([]);
      }

      enqueueSnackbar(t("Images saved successfully"), { variant: "success" });
    };

    const deleteRoomImage = (picturePath: string) => {
      setPicturePathsToDelete(prev => [...prev, picturePath]);
      setRoomPicturePaths(prev => prev.filter(path => path !== picturePath));
    };

    const handleCloseImageDialog = () => {
      setIsImageDialogOpen(false);
      setFiles([]);
      setRoomPicturePaths(prev => [...prev, ...picturePathsToDelete]);
      setPicturePathsToDelete([]);
    };

    useEffect(() => {
      // store basic information of zone schedules
      if (zoneSchedules) {
        setZoneName(zoneSchedules[0]?.inventory?.name ?? "");
        setZoneDesc(zoneSchedules[0]?.description ?? "");
        setZoneUserNumber({
          minUsers: minUsers,
          maxUsers: maxUsers
        });
      }

      // store the assigned equipments
      updateAssingedEquipment(zoneSchedules, setAssignedEquipment);

      // store the picture paths (skip fetch when zone has no inventory yet, e.g. newly created with inventoryId: 0)
      if (singleZoneSelected && isConferenceOnlySelected && zoneInventoryId > 0) {
        fetchZoneInventory(zoneInventoryId).then(data =>
          setRoomPicturePaths(data.picturePaths ?? [])
        );
      } else if (singleZoneSelected && isConferenceOnlySelected) {
        setRoomPicturePaths([]);
      }
    }, [zoneSchedules]);

    useEffect(() => {
      updateMutatedZoneInventory(mutatedZoneInventory, zoneSchedules, updateZoneInventory);
    }, [mutatedZoneInventory]);

    return (
      <InfoSideBar height={height} width={290} overflow="scroll" ref={ref}>
        {zoneSchedules.length > 0 && (
          <InfoBox color={"#00000088"}>
            {/* header of multi zones edit and listing the names */}
            <MultiInfoHeader
              type={"Zone"}
              showMultiInfoHeader={zoneSchedules.length > 1}
              onClose={onClose}
            />

            {/* name of the zone */}
            <CompactTextField
              placeholder={t("Name")}
              value={zoneName}
              rows={2}
              onChange={newText => {
                onChangeName(selectedIds, newText);
                setZoneName(newText);
              }}
              handleOk={newText => {
                for (const zone of zoneSchedules) {
                  handlePatchZoneInventory({
                    zoneInventoryId: zone.inventory?.id,
                    opts: { name: newText }
                  });
                }
              }}
              newText={zoneName}
              setNewText={(t: string) => setZoneName(t)}
              isEditable={isEditable}
              setIsFieldEditing={setIsFieldEditing}
            />

            {/* description of the zone */}
            <CompactTextField
              rows={2}
              placeholder={t("Description")}
              value={zoneDesc}
              onChange={desc => onChangeZoneDescription(desc)}
              newText={zoneDesc}
              setNewText={(t: string) => setZoneDesc(t)}
              isEditable={isEditable}
              setIsFieldEditing={setIsFieldEditing}
            />

            {/* type of the zone */}
            {isIdenticalZoneTypeSelected && (
              <AssignBox
                buttonDataTestId="type-assign-box"
                value={zoneTypeId}
                name={zoneTypeName}
                type={"Type"}
                isEditable={isEditable}
                setIsFieldEditing={setIsFieldEditing}
              />
            )}

            {/** inventory */}
            {singleZoneSelected && (
              <AssignBox
                buttonDataTestId="inventory-assign-box"
                onClickAssign={() => setIsZnInvModalOpen(true)}
                value={zoneInventoryId}
                type={"Inventory"}
                name={zoneSchedules[0].inventory?.name}
                isEditable={isEditable}
                setIsFieldEditing={setIsFieldEditing}
              />
            )}

            {/** category */}
            <AssignBox
              onClickAssign={() => setIsZoneCategoryModalOpen(true)}
              value={zoneSchedules[0].category?.id}
              type={"Category"}
              buttonDataTestId="assign-category-btn"
              name={zoneSchedules[0].category?.name}
              isEditable={isEditable}
              setIsFieldEditing={setIsFieldEditing}
            />

            {/** equipment */}
            {singleZoneSelected && (
              <AssignBox
                buttonDataTestId="equipment-assign-box"
                onClickAssign={() => setIsEquipmentModalOpen(!isEquipmentModalOpen)}
                value={
                  assignedEquipment.length
                    ? t(`${assignedEquipment.length} (pcs)`)
                    : t("NoEquipmentSidebarLabel")
                }
                type={"Equipment"}
                valueArr={assignedEquipment.map(eq => eq.name)}
                name={initMoreItemName(assignedEquipment, "Equipment", false, t)}
                isEditable={isEditable}
                setIsFieldEditing={setIsFieldEditing}
              />
            )}

            {/** equipment category */}
            <AssignBox
              buttonDataTestId="equipment-category-assign-box"
              onClickAssign={() => setIsEquipCateDialogOpen(!isEquipCateDialogOpen)}
              value={0}
              type={"Equipment_category"}
              name={initMoreItemName(
                zoneSchedules[0].equipmentCategories,
                "Equipment_category",
                false,
                t
              )}
              isEditable={isEditable}
              setIsFieldEditing={setIsFieldEditing}
            />

            {/** properties */}
            <AssignBox
              buttonDataTestId="book-properties-assign-box"
              onClickAssign={() => setIsPropertyDialogOpen(!isPropertyDialogOpen)}
              value={0}
              type={"Properties"}
              name={initMoreItemName(zoneSchedules[0].bookingProperties, "Properties", false, t)}
              isEditable={isEditable}
              setIsFieldEditing={setIsFieldEditing}
            />

            {singleZoneSelected && isConferenceOnlySelected && (
              <AssignBox
                buttonDataTestId="tandem-assign-box"
                onClickAssign={() => setIsTandemDialogOpen(true)}
                value={tandemName}
                type={"Tandem"}
                name={tandemName}
                isEditable={isEditable}
                setIsFieldEditing={setIsFieldEditing}
              />
            )}

            {/** property for conference zone */}
            {singleZoneSelected && isConferenceOnlySelected && (
              <>
                {/** room image upload and view */}
                <AssignBox
                  buttonDataTestId="show-upload-images-assign-box"
                  onClickAssign={() => setIsImageDialogOpen(!isImageDialogOpen)}
                  value={0}
                  type={"Images"}
                  name={initImageLabel(roomPicturePaths.length, t)}
                  isEditable={isEditable}
                  setIsFieldEditing={setIsFieldEditing}
                />

                {/** min and max users number */}
                <Grid2 container data-testid="zone-userNumber-input" sx={{ flexWrap: "nowrap" }}>
                  <Grid2>
                    <MiniTextInput
                      fields={[
                        {
                          value: zoneUserNumber.minUsers,
                          placeholder: "Min",
                          id: "min",
                          type: "number"
                        },
                        {
                          value: zoneUserNumber.maxUsers,
                          placeholder: "Max",
                          id: "max",
                          type: "number"
                        }
                      ]}
                      onInput={(id, value) => {
                        handleUserNumbersInput(
                          id,
                          value,
                          zoneSchedules[0].id,
                          zoneUserNumber,
                          setZoneUserNumber,
                          onChangeUserNumber,
                          enqueueSnackbar
                        );
                      }}
                      zoneUserNumber={zoneUserNumber}
                      setZoneUserNumber={(n: IZoneUserNumber) => setZoneUserNumber(n)}
                      readOnly={!isEditable}
                    />
                  </Grid2>
                  <Grid2 data-testid="user-number-info-desc" sx={{ pt: "2px", pl: "2px" }}>
                    <InfoIconWithTooltip tooltipText={t("EditInfoUserNumberForConferenceZone")} />
                  </Grid2>
                </Grid2>
              </>
            )}

            {/** Settings */}
            <AssignBox
              buttonDataTestId="settings-assign-box"
              type={"Settings"}
              setIsFieldEditing={setIsFieldEditing}
              bottomChildren={
                <Box sx={{ p: 0.5 }}>
                  <Grid2 container sx={{ placeContent: "space-between", alignItems: "center" }}>
                    {/* assign permissions; don't show when placezone is selected */}
                    {isConferenceOnlySelected && (
                      <Grid2 data-testid="assign-perm-item" display={"contents"}>
                        <Grid2 sx={{ display: "flex", alignItems: "center" }}>
                          <IconButton
                            disableFocusRipple
                            disableRipple
                            disableTouchRipple
                            sx={{ cursor: "default" }}
                          >
                            <LockPerson />
                          </IconButton>
                          <Typography sx={{ fontSize: 13, pl: 0.5 }}>{t("Permissions")}</Typography>
                        </Grid2>
                        <Grid2>
                          <ButtonStyledText
                            theme={theme}
                            data-testid={"permission-btn"}
                            sx={{ fontSize: 12 }}
                            onClick={() => setIsZnPermDialogOpen(true)}
                          >
                            {isEditable ? t("Assign") : t("Show")}
                          </ButtonStyledText>
                        </Grid2>
                      </Grid2>
                    )}

                    {/* access control */}
                    {singleZoneSelected && hasSiteId && (
                      <Grid2 data-testid="access-control-item" display={"contents"}>
                        <Grid2 sx={{ display: "flex", alignItems: "center" }}>
                          <IconButton
                            disableFocusRipple
                            disableRipple
                            disableTouchRipple
                            sx={{ cursor: "default" }}
                          >
                            <MeetingRoom />
                          </IconButton>
                          <Typography sx={{ fontSize: 13, pl: 0.5 }}>
                            {t("Access Control")}
                          </Typography>
                        </Grid2>
                        <Grid2>
                          <ButtonStyledText
                            theme={theme}
                            data-testid={"zone-access-btn"}
                            sx={{ fontSize: 12 }}
                            onClick={() => setIsAccessControlOpen(true)}
                            disabled={!zoneSchedules[0].inventory?.id}
                          >
                            {isEditable ? t("Assign") : t("Show")}
                          </ButtonStyledText>
                        </Grid2>
                      </Grid2>
                    )}
                  </Grid2>

                  {isConferenceOnlySelected && (
                    <Divider sx={{ mt: 0.5, mb: 0.5, opacity: "0.6" }} />
                  )}

                  <Box display={"flex"} flexDirection={"column"}>
                    <FormControlLabel
                      labelPlacement={"end"}
                      sx={{ fontSize: "12px", ml: 0, mr: 0, mb: 0.5 }}
                      control={
                        <Switch
                          slotProps={{ input: { "data-testid": "enabled-switch" } as any }}
                          size={"small"}
                          sx={{ marginRight: 1 }}
                          disabled={!isEditable}
                          checked={!zoneSchedules[0].disabled}
                          onChange={e => onChangeEnabledStatus(selectedIds, e.target.checked)}
                        />
                      }
                      label={
                        <Typography fontSize={"12px"}>
                          {generateDisabledLabel(!zoneSchedules[0].disabled)}
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      labelPlacement={"end"}
                      sx={{ fontSize: "12px", ml: 0, mr: 0, mb: 0.5 }}
                      control={
                        <Switch
                          data-testid={"approvalRequired-switch"}
                          size={"small"}
                          sx={{ marginRight: 1 }}
                          disabled={!isEditable}
                          checked={zoneSchedules[0].approvalRequired}
                          onChange={e =>
                            onChangeApproval(selectedIds, { approvalRequired: e.target.checked })
                          }
                        />
                      }
                      label={
                        <Typography fontSize={"12px"}>
                          {generateApprovalRequiredLabel(zoneSchedules[0].approvalRequired)}
                        </Typography>
                      }
                    />
                    {zoneSchedules[0].approvalRequired && (
                      <FormControlLabel
                        labelPlacement={"end"}
                        sx={{ fontSize: "12px", ml: 0, mr: 0, mb: 0.5 }}
                        control={
                          <Switch
                            data-testid={"automatedApproval-switch"}
                            size={"small"}
                            sx={{ marginRight: 1 }}
                            disabled={!isEditable}
                            checked={zoneSchedules[0].automatedApproval}
                            onChange={e =>
                              onChangeApproval(selectedIds, { automatedApproval: e.target.checked })
                            }
                          />
                        }
                        label={
                          <Typography fontSize={"12px"}>
                            {generateAutomatedApprovalLabel(zoneSchedules[0].automatedApproval)}
                          </Typography>
                        }
                      />
                    )}
                  </Box>
                </Box>
              }
            />

            {/** bottom bar */}
            <Box
              display={"flex"}
              alignItems={"center"}
              data-testid="bottom-bar-box"
              sx={{ pr: 1, justifyContent: "space-between" }}
            >
              {/* qr code print for conference zone */}
              {isConferenceOnlySelected && (
                <Box>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      data-testid="tool-btn-printQr"
                      onClick={() => setIsQrModalOpen(!isQrModalOpen)}
                    >
                      <Icon icon={qrcodeIcon} color={iconColor} width={26} height={26} />
                    </IconButton>
                    <Typography fontSize={"12px"}>
                      {zoneSchedules.length > 1 ? t("MultiQrCodePrint") : t("SingleQrCodePrint")}
                    </Typography>
                  </div>
                </Box>
              )}

              {isEditable && (
                <Box display={"flex"} alignItems={"center"}>
                  <IconButton data-testid={"delete-btn"} onClick={() => onClickDelete(selectedIds)}>
                    <Delete />
                  </IconButton>
                  <Typography fontSize={"12px"}> {t("Delete")}</Typography>
                </Box>
              )}
            </Box>

            {/* zone permission modal */}
            <Modal
              open={isZnPermDialogOpen}
              onClose={() => setIsZnPermDialogOpen(false)}
              sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              <Box>
                <ZonePermissionDialogContent
                  selectedZones={zoneSchedules}
                  onChangePerm={onChangePerm}
                  onClose={() => setIsZnPermDialogOpen(false)}
                  remoteUnits={remoteUnits}
                  isRemoteUnitsFetching={isRemoteUnitsFetching}
                  isEditable={isEditable}
                />
              </Box>
            </Modal>

            {/* zone inventory modal */}
            <Modal
              open={isZnInvModalOpen}
              onClose={() => setIsZnInvModalOpen(false)}
              sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              <Box>
                <ZoneInventoryDialogContent
                  selectedZone={zoneSchedules[0]}
                  floorPlan={floorPlan}
                  inventoryManagement={inventoryManagement}
                  handleCreateZoneInventory={handleCreateZoneInventory}
                  onClose={() => setIsZnInvModalOpen(false)}
                  setZoneName={(n: string) => setZoneName(n)}
                />
              </Box>
            </Modal>

            {/* zone category modal */}
            <Modal
              open={isZoneCategoryModalOpen}
              onClose={() => setIsZoneCategoryModalOpen(false)}
              sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              <Box>
                <OptionDialogContent
                  pivotEntityName="Zone"
                  subjectEntityName="Category"
                  onClose={() => setIsZoneCategoryModalOpen(false)}
                  displayHeaderId={zoneSchedules[0].id}
                  displayChipValue={zoneSchedules[0].category?.name || 0}
                  subjectEntity={zoneSchedules}
                  subjectEntityEntries={floorPlan.zoneCategories}
                  updateFunctionSub1={categoryManagement.changeZoneCategory}
                />
              </Box>
            </Modal>

            {/* zone Equipment assignment modal */}
            <Modal
              open={isEquipmentModalOpen}
              onClose={() => setIsEquipmentModalOpen(false)}
              sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              <Box>
                <OptionDialogContent
                  subjectEntity={zoneSchedules[0]}
                  subjectEntityName={"Equipment"}
                  displayHeaderId={zoneSchedules[0].id}
                  displayChipValue={""}
                  subjectEntityEntries={[
                    { availableEquipment: availableEquipment, assignedEquipment: assignedEquipment }
                  ]}
                  pivotEntityName="Zone"
                  onClose={() => setIsEquipmentModalOpen(false)}
                  updateFunctionSub2={equipmentManagement.changeZoneEquipments}
                />
              </Box>
            </Modal>

            {/* equipment category modal */}
            <Modal
              open={isEquipCateDialogOpen}
              /* istanbul ignore next */
              onClose={() => setIsEquipCateDialogOpen(false)}
              sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              <Box>
                <AssignEntryTableContainer
                  subjectEntity={zoneSchedules}
                  tableChild={
                    <EquipmentCategoryAssignTable
                      subjectEntity={zoneSchedules}
                      entries={floorPlan.equipmentCategories}
                      onAssign={equipCate => onChangeEquipCate(selectedIds, equipCate)}
                      onClose={() => setIsEquipCateDialogOpen(false)}
                    />
                  }
                  type={t("Equipment category")}
                  onClose={() => setIsEquipCateDialogOpen(false)}
                />
              </Box>
            </Modal>

            <Modal
              open={isTandemDialogOpen}
              onClose={() => setIsTandemDialogOpen(false)}
              sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              <Box>
                <TandemAssignDialogContent
                  self={tandemSelf}
                  floorPlan={floorPlan}
                  onClose={() => setIsTandemDialogOpen(false)}
                  inventoryManagement={inventoryManagement}
                />
              </Box>
            </Modal>

            {/* properties modal */}
            <Modal
              open={isPropertyDialogOpen}
              /* istanbul ignore next */
              onClose={() => setIsPropertyDialogOpen(false)}
              sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              <Box>
                <AssignEntryTableContainer
                  subjectEntity={zoneSchedules}
                  tableChild={
                    <BookingPropertyAssignTable
                      subjectEntity={zoneSchedules}
                      entries={floorPlan.bookingProperties?.filter(prop => prop.usedForZone)}
                      onAssign={bookProp => onChangeBookProp(selectedIds, bookProp)}
                      onClose={() => setIsPropertyDialogOpen(false)}
                    />
                  }
                  type={t("Properties")}
                  onClose={() => setIsPropertyDialogOpen(false)}
                />
              </Box>
            </Modal>

            {/** image dialog when isEditable */}
            <EnlargedImageCarouselUploadDialog
              picturePaths={roomPicturePaths}
              files={files}
              setFiles={setFiles}
              picturePathsToDelete={picturePathsToDelete}
              saveRoomImages={saveRoomImages}
              handleCloseDialog={handleCloseImageDialog}
              handleDelete={deleteRoomImage}
              isOpen={isImageDialogOpen && isEditable}
              isLoading={fetchZoneInventoryLoading}
            />

            {/** image dialog when isEditable not */}
            <EnlargedImageCarouselDialog
              handleClose={() => setIsImageDialogOpen(false)}
              isOpen={isImageDialogOpen && !isEditable}
              isLoading={fetchZoneInventoryLoading}
              picturePaths={roomPicturePaths}
            />

            {/* access control modal */}
            <Modal
              open={isAccessControlOpen}
              /* istanbul ignore next */
              onClose={() => setIsAccessControlOpen(false)}
              sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              <Box>
                <ZoneAccessControlDialogContent
                  selectedZone={zoneSchedules[0]}
                  onClose={() => setIsAccessControlOpen(false)}
                  isEditable={isEditable}
                />
              </Box>
            </Modal>

            {/** print QR Code modal for conference zone */}
            <QrCodeComponent
              open={isQrModalOpen}
              floorPlanName={floorPlanName}
              entries={zoneSchedules}
              onClose={() => setIsQrModalOpen(false)}
            />
          </InfoBox>
        )}
      </InfoSideBar>
    );
  }
);
